/**
 * Contract ("Verträge") data hooks (Phase 6, §4.1/§6.1).
 *
 * The single `['contracts']` cache holds the full contract list; forecast and
 * suggestions live under the same `['contracts', ...]` prefix
 * (`qk.contractForecast(month)` / `qk.contractSuggestions`), so every mutation
 * below invalidates the bare `qk.contracts` key — TanStack Query treats an
 * invalidated key as a prefix match, which also refreshes forecast +
 * suggestions without listing them individually. Attach/detach additionally
 * invalidate `['transactions']` because they change `TransactionOut.contract_id`.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../api.ts';
import { qk } from '../queryKeys.ts';
import type {
  ContractCreate,
  ContractForecast,
  ContractOut,
  ContractSuggestion,
  ContractUpdate,
  MonthStr,
} from '../types.ts';

const STALE = 5 * 60 * 1000;

// ── Queries ────────────────────────────────────────────────────────────────

/** All contracts, active-first then name (server order, §4.1). */
export function useContracts(): UseQueryResult<ContractOut[]> {
  return useQuery({
    queryKey: qk.contracts,
    queryFn: () => api.get<ContractOut[]>('/api/contracts'),
    staleTime: STALE,
  });
}

export function useContractForecast(month: MonthStr): UseQueryResult<ContractForecast> {
  return useQuery({
    queryKey: qk.contractForecast(month),
    queryFn: () => api.get<ContractForecast>(`/api/contracts/forecast?month=${month}`),
    staleTime: STALE,
  });
}

export function useContractSuggestions(): UseQueryResult<ContractSuggestion[]> {
  return useQuery({
    queryKey: qk.contractSuggestions,
    queryFn: () => api.get<ContractSuggestion[]>('/api/contracts/suggestions'),
    staleTime: STALE,
  });
}

// ── Mutations ──────────────────────────────────────────────────────────────

export function useCreateContract(): UseMutationResult<ContractOut, Error, ContractCreate> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: ContractCreate) => api.post<ContractOut>('/api/contracts', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contracts });
    },
  });
}

export function useUpdateContract(): UseMutationResult<
  ContractOut,
  Error,
  { id: number; body: ContractUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ContractUpdate }) =>
      api.patch<ContractOut>(`/api/contracts/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contracts });
    },
  });
}

export function useDeleteContract(): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.del(`/api/contracts/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contracts });
    },
  });
}

export function useAttachContractTransaction(): UseMutationResult<
  ContractOut,
  Error,
  { id: number; transactionId: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transactionId }: { id: number; transactionId: number }) =>
      api.post<ContractOut>(`/api/contracts/${id}/transactions/${transactionId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contracts });
      void qc.invalidateQueries({ queryKey: qk.transactions });
    },
  });
}

// NOTE: the API returns the updated `ContractOut` from this DELETE (§4.1), but
// `api.del` (like every other delete hook in this codebase, cf. `useRules.ts`)
// discards the response body — callers refetch via the `['contracts']`
// invalidation below rather than reading a returned value.
export function useDetachContractTransaction(): UseMutationResult<
  void,
  Error,
  { id: number; transactionId: number }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, transactionId }: { id: number; transactionId: number }) =>
      api.del(`/api/contracts/${id}/transactions/${transactionId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.contracts });
      void qc.invalidateQueries({ queryKey: qk.transactions });
    },
  });
}
